"use client";

import { useState } from "react";
import { AutocompleteInput } from "@/components/search/autocomplete-input";
import { Icon } from "@/components/ui/icon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  addVariant,
  MAX_ENTITY_VARIANT_LEN,
  MAX_ENTITY_VARIANTS,
  removeVariant,
  type AddVariantRejection,
  type VariantBlock,
  type VariantField,
} from "@/lib/search/entity-variants";
import { cn } from "@/lib/utils";

const REJECTION_MESSAGES: Record<Exclude<AddVariantRejection, "empty">, string> = {
  duplicate: "Already one of this chip's forms",
  too_long: `Variants are limited to ${MAX_ENTITY_VARIANT_LEN} characters`,
  limit: `At most ${MAX_ENTITY_VARIANTS} variants per chip`,
};

/**
 * Edits the alias `variants` of one entity chip: the chip then matches when
 * its text OR any variant matches. `compact` renders an icon-only trigger for
 * single-row chips; the default is a footer trigger styled like `ScopeDropdown`.
 */
export function VariantsDropdown<T extends VariantBlock>({
  field,
  item,
  onChange,
  compact = false,
}: {
  field: VariantField;
  item: T;
  onChange: (next: T) => void;
  compact?: boolean;
}) {
  const [rejection, setRejection] = useState<string | null>(null);
  const variants = item.variants ?? [];
  const count = variants.length;
  const atLimit = count >= MAX_ENTITY_VARIANTS;
  const triggerLabel =
    count === 0 ? "Add variants" : `${count} variant${count === 1 ? "" : "s"}`;

  const add = (raw: string) => {
    const result = addVariant(item, raw);
    if (result.ok) {
      setRejection(null);
      onChange(result.item);
    } else {
      setRejection(result.reason === "empty" ? null : REJECTION_MESSAGES[result.reason]);
    }
  };

  return (
    <Popover onOpenChange={() => setRejection(null)}>
      <PopoverTrigger asChild>
        {compact ? (
          <button
            type="button"
            aria-label={triggerLabel}
            title={triggerLabel}
            className="shrink-0 cursor-pointer transition-opacity"
          >
            <Icon
              name="IconLayersTwo"
              size={12}
              className={count > 0 ? "text-ai" : "text-contrast opacity-30 hover:opacity-60"}
            />
          </button>
        ) : (
          <button
            type="button"
            aria-label={triggerLabel}
            className="flex cursor-pointer items-center gap-0.5"
          >
            <span
              className={cn(
                "whitespace-nowrap text-[11px] tracking-[-0.1px]",
                count > 0 ? "text-ai" : "text-contrast/40 hover:text-contrast/70",
              )}
            >
              {triggerLabel}
            </span>
            <Icon
              name="IconChevronDownMedium"
              size={11}
              className={count > 0 ? "text-ai" : "text-contrast/40"}
            />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={2} className="flex w-[260px] flex-col gap-1.5 p-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] tracking-[-0.1px] text-contrast/60">
            Also matches any of
          </span>
          <span className="text-[11px] tracking-[-0.1px] text-contrast/40">
            {count}/{MAX_ENTITY_VARIANTS}
          </span>
        </div>
        {count > 0 && (
          <ul className="flex flex-col gap-0.5">
            {variants.map((variant, i) => (
              <li
                key={variant}
                className="flex h-6 items-center gap-1 rounded bg-contrast/4 px-1.5"
              >
                <span className="min-w-0 flex-1 truncate text-xs text-contrast/80" title={variant}>
                  {variant}
                </span>
                <button
                  type="button"
                  aria-label={`Remove variant ${variant}`}
                  onClick={() => {
                    setRejection(null);
                    onChange(removeVariant(item, i));
                  }}
                  className="shrink-0 cursor-pointer opacity-40 transition-opacity hover:opacity-70"
                >
                  <Icon name="IconCrossMedium" size={12} className="text-heading" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <AutocompleteInput
          variant="filter"
          field={field}
          placeholder={atLimit ? "Variant limit reached" : "Add variant..."}
          responsive={false}
          enterCommitsFreeText
          disabled={atLimit}
          onSelect={(result) => add(result.value)}
          onFreeText={add}
        />
        {rejection && (
          <p role="status" className="text-[11px] tracking-[-0.1px] text-destructive/80">
            {rejection}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
